import { z } from "zod";

export const ADMIN_REQUEST_TYPES = ["document", "work", "policy", "other"] as const;
export const AdminRequestTypeSchema = z.enum(ADMIN_REQUEST_TYPES);
export type AdminRequestType = z.infer<typeof AdminRequestTypeSchema>;

export const ADMIN_REQUEST_REVIEW_LEVELS = [
  "senior_administration",
  "head_administration",
] as const;
export const AdminRequestReviewLevelSchema = z.enum(ADMIN_REQUEST_REVIEW_LEVELS);
export type AdminRequestReviewLevel = z.infer<typeof AdminRequestReviewLevelSchema>;

export const ADMIN_REQUEST_STATUSES = [
  "pending",
  "changes_requested",
  "approved",
  "denied",
  "withdrawn",
] as const;
export const AdminRequestStatusSchema = z.enum(ADMIN_REQUEST_STATUSES);
export type AdminRequestStatus = z.infer<typeof AdminRequestStatusSchema>;

export const AdminRequestLinkSchema = z.object({
  label: z.string().trim().min(1).max(120),
  url: z
    .string()
    .url()
    .max(2048)
    .refine((value) => /^https?:\/\//i.test(value), "Only HTTP and HTTPS links are supported"),
});

const AdminRequestContentSchema = z.object({
  type: z.literal("document"),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(20_000),
  links: z.array(AdminRequestLinkSchema).max(20).default([]),
  departmentId: z.number().int().positive().nullish(),
});

export const CreateAdminRequestBodySchema = AdminRequestContentSchema.extend({
  reviewLevel: AdminRequestReviewLevelSchema.default("senior_administration"),
  preferredReviewerId: z.number().int().positive().nullish(),
});
export type CreateAdminRequestBody = z.infer<typeof CreateAdminRequestBodySchema>;

export const ResubmitAdminRequestBodySchema = AdminRequestContentSchema.extend({
  expectedRevision: z.number().int().positive(),
});
export type ResubmitAdminRequestBody = z.infer<typeof ResubmitAdminRequestBodySchema>;

export const AdminRequestDecisionBodySchema = z
  .object({
    decision: z.enum(["approved", "denied", "changes_requested"]),
    note: z.string().trim().max(10_000).nullish(),
    expectedRevision: z.number().int().positive(),
  })
  .refine((value) => value.decision === "approved" || Boolean(value.note?.trim()), {
    message: "A note is required when denying a request or asking for changes",
    path: ["note"],
  });
export type AdminRequestDecisionBody = z.infer<typeof AdminRequestDecisionBodySchema>;

export const AdminRequestCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(10_000),
});
export type AdminRequestCommentBody = z.infer<typeof AdminRequestCommentBodySchema>;

export const AdminRequestAttachmentSchema = z.object({
  id: z.string().uuid(),
  revisionId: z.number().int(),
  originalName: z.string(),
  mimetype: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type AdminRequestAttachment = z.infer<typeof AdminRequestAttachmentSchema>;

export const AdminRequestRevisionSchema = z.object({
  id: z.number().int(),
  revisionNumber: z.number().int().positive(),
  type: AdminRequestTypeSchema,
  title: z.string(),
  description: z.string(),
  reason: z.string(),
  proposedText: z.string().nullable(),
  links: z.array(AdminRequestLinkSchema),
  departmentId: z.number().int().nullable(),
  departmentName: z.string().nullable(),
  createdById: z.number().int().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  attachments: z.array(AdminRequestAttachmentSchema),
});
export type AdminRequestRevision = z.infer<typeof AdminRequestRevisionSchema>;

export const AdminRequestCommentSchema = z.object({
  id: z.number().int(),
  authorId: z.number().int().nullable(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});
export type AdminRequestComment = z.infer<typeof AdminRequestCommentSchema>;

export const AdminRequestSchema = z.object({
  id: z.number().int(),
  status: AdminRequestStatusSchema,
  requesterId: z.number().int().nullable(),
  requesterName: z.string().nullable(),
  preferredReviewerId: z.number().int().nullable(),
  preferredReviewerName: z.string().nullable(),
  reviewLevel: AdminRequestReviewLevelSchema,
  currentRevision: z.number().int().positive(),
  reviewerId: z.number().int().nullable(),
  reviewerName: z.string().nullable(),
  decisionNote: z.string().nullable(),
  submittedAt: z.string(),
  decidedAt: z.string().nullable(),
  withdrawnAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  revision: AdminRequestRevisionSchema,
});
export type AdminRequest = z.infer<typeof AdminRequestSchema>;

export const AdminRequestDetailSchema = AdminRequestSchema.extend({
  revisions: z.array(AdminRequestRevisionSchema),
  comments: z.array(AdminRequestCommentSchema),
  canReview: z.boolean().optional(),
});
export type AdminRequestDetail = z.infer<typeof AdminRequestDetailSchema>;

export const AdminRequestListItemSchema = AdminRequestSchema.omit({ revision: true }).extend({
  type: AdminRequestTypeSchema,
  title: z.string(),
  description: z.string(),
  departmentName: z.string().nullable(),
});
export type AdminRequestListItem = z.infer<typeof AdminRequestListItemSchema>;

export const AdminRequestListQuerySchema = z.object({
  q: z.string().max(200).optional(),
  status: AdminRequestStatusSchema.optional(),
  type: AdminRequestTypeSchema.optional(),
  mine: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional()
    .default(false),
  awaiting: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional()
    .default(false),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});
export type AdminRequestListQuery = z.infer<typeof AdminRequestListQuerySchema>;

export const AdminRequestPageSchema = z.object({
  items: z.array(AdminRequestListItemSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type AdminRequestPage = z.infer<typeof AdminRequestPageSchema>;

export const AdminRequestReviewerSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  role: z.string(),
});
export type AdminRequestReviewer = z.infer<typeof AdminRequestReviewerSchema>;

export const AdminRequestReviewerSettingsSchema = z.object({
  reviewers: z.array(AdminRequestReviewerSchema),
  selectedReviewerIds: z.array(z.number().int()),
  discordUserIds: z.record(z.string(), z.string()),
  isCustomized: z.boolean(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
});
export type AdminRequestReviewerSettings = z.infer<typeof AdminRequestReviewerSettingsSchema>;

export const UpdateAdminRequestReviewerSettingsBodySchema = z.object({
  reviewerIds: z.array(z.number().int().positive()).min(1).max(100),
  discordUserIds: z
    .record(z.string().regex(/^[1-9]\d*$/), z.string().regex(/^\d{17,20}$/))
    .default({}),
});
export type UpdateAdminRequestReviewerSettingsBody = z.infer<
  typeof UpdateAdminRequestReviewerSettingsBodySchema
>;

export const AdminRequestReviewNoteSchema = z.object({
  note: z.string().trim().min(1).max(10_000),
});
